<template>
  <div id="integrations" class="flex flex-col gap-6">
    <h1 class="text-xl text_color font-semibold">Integrations</h1>
    <div
      class="flex flex-col overflow-y-scroll gap-3"
      style="height: calc(100vh - (180px + var(--banner-height)))"
    >
      <div
        v-for="integration in integrationList"
        :key="integration.key"
        class="integration flex justify-between items-center rounded-lg"
      >
        <div class="flex items-center m-4 gap-4">
          <img
            :src="integration.image_url"
            alt="integration_icons"
            class="size-[52px]"
          />
          <div class="flex flex-col gap-[6px]">
            <span class="proxima_nova text-base font-semibold">
              {{ formatIntegrationName(integration.name) }}
            </span>
            <span class="proxima_nova text-sm font-normal secondary_color">
              {{ integration.description }}
            </span>
          </div>
        </div>

        <div class="mr-4">
          <a-button
            v-if="isManageable(integration)"
            @click="toggleIntegration(integration)"
            class="secondary_medium"
          >
            Manage
          </a-button>

          <a-tooltip
            placement="top"
            v-else-if="
              emailIntegrationConnected &&
              emailIntegrationConnected?.name !== integration?.name &&
              emailIntegrations.includes(integration?.name)
            "
          >
            <template #title>An email integration already exists.</template>
            <a-button class="primary_medium" disabled>Connect</a-button>
          </a-tooltip>

          <a-button
            v-else-if="integration?.status !== integrationStatus.COMPLETE"
            @click="toggleIntegration(integration)"
            class="primary_medium"
          >
            Connect
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import { useRouter, useRoute } from "vue-router";
import { toTitleCase } from "@/src/utils/utils.js";
import {
  emailIntegrations,
  integrationConnectType,
  integrationStatus,
  manageIntegrationsConnectType,
  integrations,
} from "@/src/constants/settingConstants.js";

const integrationList = ref([]);
const router = useRouter();
const route = useRoute();
const emailIntegrationConnected = ref(null);

const isManageable = (integration) => {
  return (
    (manageIntegrationsConnectType.includes(integration?.connect_type) ||
      integration?.name === integrations.LINKEDIN ||
      integration?.name === integrations.SLACK) &&
    integration?.status === integrationStatus.COMPLETE
  );
};

const fetchIntegrations = async () => {
  try {
    const response = await DashboardService.getIntegrations();
    if (response.success) {
      integrationList.value = response.integrations.filter(
        (integration) => !emailIntegrations.includes(integration?.name),
      );
    } else {
      message.error("Failed to fetch integration");
    }
  } catch (error) {
    console.error("Error fetching integration:", error);
    message.error("An error occurred while fetching integration");
  }
};

const toggleIntegration = (integration) => {
  if (
    integration.status !== integrationStatus.COMPLETE &&
    (integration?.connect_type == "SALESFORCE" ||
      integration?.connect_type == "HUBSPOT" ||
      integration?.connect_type === integrationConnectType.GOOGLE_CALENDAR ||
      integration?.connect_type === integrationConnectType.ZOOM ||
      integration?.connect_type === integrationConnectType.ZOHO)
  ) {
    connectIntegration(integration);
    return;
  }
  if (
    manageIntegrationsConnectType.includes(integration?.connect_type) ||
    (integration?.name === integrations.LINKEDIN &&
      integration?.status === integrationStatus.COMPLETE) ||
    (integration?.name === integrations.SLACK &&
      integration?.status === integrationStatus.COMPLETE) ||
    (integration?.name === integrations.ZOOM &&
      integration?.status === integrationStatus.COMPLETE)
  ) {
    router.push({
      name: "integration-details",
      params: { name: integration.name.toLowerCase() },
    });
  } else if (integration.status !== integrationStatus.COMPLETE) {
    connectIntegration(integration);
  } else {
    router.push({
      name: "manage-integration",
      params: { name: integration.name.toLowerCase() },
    });
  }
};

const connectIntegration = async (integration) => {
  try {
    const response = await DashboardService.connectIntegration(integration.id);
    if (response.success) {
      if (integration.name.toLowerCase() === "slack") {
        window.open(response.redirect_url, "_self");
      } else {
        window.open(response.redirect_url);
      }
      fetchIntegrations().then().catch();
    } else {
      message.error(`Failed to connect ${integration.name}`);
    }
  } catch (error) {
    console.error(`Error connecting ${integration.name}:`, error);
    message.error(`An error occurred while connecting ${integration.name}`);
  }
};

const disconnectIntegration = async (integration) => {
  try {
    const response = await DashboardService.disconnectIntegration(
      integration.key,
    );
    if (response.success) {
      integration.connected = false;
      message.success(`${integration.name} disconnected successfully`);
    } else {
      message.error(`Failed to disconnect ${integration.name}`);
    }
  } catch (error) {
    console.error(`Error disconnecting ${integration.name}:`, error);
    message.error(`An error occurred while disconnecting ${integration.name}`);
  }
};

const handleIntegrationSuccess = () => {
  if (route.query.success && route.query.integration) {
    const integrationName = route.query.integration;
    if (route.query.success === "true") {
      message.success(`${toTitleCase(integrationName)} connected successfully`);
    } else if (route.query.success === "false") {
      message.error(
        `${toTitleCase(integrationName)} couldn't be connected. Please try again`,
      );
    }
    removeQueryParams();
  }
};

const formatIntegrationName = (name) => {
  return name.toUpperCase() === "READ_AI" ? "Read AI" : toTitleCase(name);
};

const removeQueryParams = () => {
  router.replace({
    path: route.path,
    hash: route.hash,
    query: {},
  });
};

onMounted(() => {
  fetchIntegrations();
  if ("success" in route.query && "integration" in route.query) {
    handleIntegrationSuccess();
  }
});
</script>

<style scoped>
.integration {
  background-color: var(--white-opacity-4);
  border: 1px solid var(--white-opacity-8);
}
</style>
